'use client';
import * as React from 'react';
import useEnhancedEffect from '@mui/utils/useEnhancedEffect';
import useEventCallback from '@mui/utils/useEventCallback';
import { useStoreEffect } from '@mui/x-internals/useStoreEffect';
import { useAssertModelConsistency } from '@mui/x-internals/useAssertModelConsistency';
import { errorOnce } from '@mui/x-internals/warning';
import type { PointerGestureEventData } from '@mui/x-internal-gestures/core';
import type { ChartPlugin } from '../../models';
import type { UseChartCartesianAxisSignature } from './useChartCartesianAxis.types';
import { rainbowSurgePalette } from '../../../../colorPalettes';
import { selectorChartSeriesProcessed } from '../../corePlugins/useChartSeries/useChartSeries.selectors';
import { defaultizeXAxis, defaultizeYAxis } from './defaultizeAxis';
import { selectorChartXAxis, selectorChartYAxis } from './useChartCartesianAxisRendering.selectors';
import { getAxisIndex } from './getAxisValue';
import { getChartPoint } from '../../../getChartPoint';
import { selectorChartsInteractionIsInitialized } from '../useChartInteraction';
import { selectorChartAxisInteraction } from './useChartCartesianInteraction.selectors';
import { checkHasInteractionPlugin } from '../useChartInteraction/checkHasInteractionPlugin';
import { getAxisClickPayload } from './getAxisClickPayload';
import { useDefaultTickLabelStyle } from '../../../useDefaultTickLabelStyle';
import type { ChartsActivationEvent } from '../../../../models/events';
import type { ComputeResult } from './computeAxisValue';
import type { AxisItemIdentifier, ChartsAxisProps } from '../../../../models/axis';
import {
  isItemActivationKey,
  selectorChartsIsKeyboardActivationEnabled,
  selectorChartsKeyboardXAxisIndex,
  selectorChartsKeyboardYAxisIndex,
} from '../useChartKeyboardNavigation';

/**
 * Narrows an axis item to one the axis actually has a value for.
 * Axes unrelated to the focused series can exist with empty data, so the index must resolve.
 */
function hasAxisValueAt(
  axes: ComputeResult<ChartsAxisProps>['axis'],
  item: AxisItemIdentifier | undefined,
): item is AxisItemIdentifier & { dataIndex: number } {
  return (
    item !== undefined &&
    item.dataIndex !== undefined &&
    axes[item.axisId]?.data?.[item.dataIndex] !== undefined
  );
}

export const useChartCartesianAxis: ChartPlugin<UseChartCartesianAxisSignature<any>> = ({
  params,
  store,
  instance,
}) => {
  const { chartsLayerContainerRef } = instance;
  const { xAxis, yAxis, dataset, onHighlightedAxisChange, onTooltipAxisChange, axesGap } = params;

  const defaultTickLabelStyle = useDefaultTickLabelStyle();

  useEnhancedEffect(() => {
    store.set('cartesianAxis', { ...store.state.cartesianAxis, defaultTickLabelStyle });
  }, [defaultTickLabelStyle, store]);

  if (process.env.NODE_ENV !== 'production') {
    const ids = [...(xAxis ?? []), ...(yAxis ?? [])]
      .filter((axis) => axis.id)
      .map((axis) => axis.id);
    const duplicates = new Set(ids.filter((id, index) => ids.indexOf(id) !== index));
    if (duplicates.size > 0) {
      errorOnce(
        [
          `MUI X Charts: The following axis ids are duplicated: ${Array.from(duplicates).join(', ')}.`,
          `Please make sure that each axis has a unique id.`,
        ].join('\n'),
      );
    }
  }

  const processedSeries = store.use(selectorChartSeriesProcessed);

  const isInteractionEnabled = store.use(selectorChartsInteractionIsInitialized);
  const { axis: xAxisWithScale, axisIds: xAxisIds } = store.use(selectorChartXAxis);
  const { axis: yAxisWithScale, axisIds: yAxisIds } = store.use(selectorChartYAxis);

  useAssertModelConsistency({
    warningPrefix: 'MUI X Charts',
    componentName: 'Chart',
    propName: 'highlightedAxis',
    controlled: params.highlightedAxis,
    defaultValue: undefined,
  });

  useAssertModelConsistency({
    warningPrefix: 'MUI X Charts',
    componentName: 'Chart',
    propName: 'tooltipAxis',
    controlled: params.tooltipAxis,
    defaultValue: undefined,
  });

  useEnhancedEffect(() => {
    if (params.highlightedAxis !== undefined) {
      store.set('controlledCartesianAxisHighlight', params.highlightedAxis);
    }
  }, [store, params.highlightedAxis]);

  useEnhancedEffect(() => {
    if (params.tooltipAxis !== undefined) {
      store.set('controlledCartesianAxisTooltip', params.tooltipAxis);
    }
  }, [store, params.tooltipAxis]);

  // The effect do not track any value defined synchronously during the 1st render by hooks called after `useChartCartesianAxis`
  // As a consequence, the state generated by the 1st run of this useEffect will always be equal to the initialization one
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    store.set('cartesianAxis', {
      axesGap,
      x: defaultizeXAxis(xAxis, dataset, axesGap),
      y: defaultizeYAxis(yAxis, dataset, axesGap),
      defaultTickLabelStyle,
    });
  }, [xAxis, yAxis, dataset, axesGap, defaultTickLabelStyle, store]);

  const usedXAxis = xAxisIds[0];
  const usedYAxis = yAxisIds[0];

  useStoreEffect(
    store,
    selectorChartAxisInteraction,
    (prevAxisInteraction, nextAxisInteraction) => {
      if (!onHighlightedAxisChange && !onTooltipAxisChange) {
        return;
      }
      if (Object.is(prevAxisInteraction, nextAxisInteraction)) {
        return;
      }

      if (prevAxisInteraction.length !== nextAxisInteraction.length) {
        onHighlightedAxisChange?.(nextAxisInteraction);
        onTooltipAxisChange?.(nextAxisInteraction);
        return;
      }
      if (
        prevAxisInteraction?.some(
          ({ axisId, dataIndex }, itemIndex) =>
            nextAxisInteraction[itemIndex].axisId !== axisId ||
            nextAxisInteraction[itemIndex].dataIndex !== dataIndex,
        )
      ) {
        onHighlightedAxisChange?.(nextAxisInteraction);
        onTooltipAxisChange?.(nextAxisInteraction);
      }
    },
  );

  const hasInteractionPlugin = checkHasInteractionPlugin(instance);

  React.useEffect(() => {
    const element = chartsLayerContainerRef.current;
    if (!isInteractionEnabled || !hasInteractionPlugin || !element || params.disableAxisListener) {
      return () => {};
    }

    // Clean the interaction when the mouse leaves the chart.
    const moveEndHandler = instance.addInteractionListener('moveEnd', (event) => {
      if (!event.detail.activeGestures.pan) {
        instance.cleanInteraction();
      }
    });
    const panEndHandler = instance.addInteractionListener('panEnd', (event) => {
      if (!event.detail.activeGestures.move) {
        instance.cleanInteraction();
      }
    });
    const pressEndHandler = instance.addInteractionListener('quickPressEnd', (event) => {
      if (!event.detail.activeGestures.move && !event.detail.activeGestures.pan) {
        instance.cleanInteraction();
      }
    });

    const gestureHandler = (event: CustomEvent<PointerGestureEventData>) => {
      const srvEvent = event.detail.srcEvent;
      const target = event.detail.target as SVGElement | undefined;
      const svgPoint = getChartPoint(element, srvEvent);

      // Release the pointer capture if we are panning, as this would cause the tooltip to
      // be locked to the first "section" it touches.
      if (
        event.detail.srcEvent.buttons >= 1 &&
        target?.hasPointerCapture(event.detail.srcEvent.pointerId) &&
        !target?.closest('[data-charts-zoom-slider]')
      ) {
        target?.releasePointerCapture(event.detail.srcEvent.pointerId);
      }
      if (!instance.isPointInside(svgPoint.x, svgPoint.y, target)) {
        instance.cleanInteraction?.();

        return;
      }
      instance.setPointerCoordinate(svgPoint);
    };

    const moveHandler = instance.addInteractionListener('move', gestureHandler);
    const panHandler = instance.addInteractionListener('pan', gestureHandler);
    const pressHandler = instance.addInteractionListener('quickPress', gestureHandler);

    return () => {
      moveHandler.cleanup();
      moveEndHandler.cleanup();
      panHandler.cleanup();
      panEndHandler.cleanup();
      pressHandler.cleanup();
      pressEndHandler.cleanup();
    };
  }, [
    chartsLayerContainerRef,
    store,
    xAxisWithScale,
    usedXAxis,
    yAxisWithScale,
    usedYAxis,
    instance,
    params.disableAxisListener,
    isInteractionEnabled,
    hasInteractionPlugin,
  ]);

  React.useEffect(() => {
    const element = chartsLayerContainerRef.current;
    const onAxisClick = params.onAxisClick;
    if (element === null || !onAxisClick) {
      return () => {};
    }

    const axisClickHandler = instance.addInteractionListener('tap', (event) => {
      const svgPoint = getChartPoint(element, event.detail.srcEvent);

      const xIndex = getAxisIndex(xAxisWithScale[usedXAxis], svgPoint.x);
      const isXAxis = xIndex !== -1;

      const dataIndex = isXAxis ? xIndex : getAxisIndex(yAxisWithScale[usedYAxis], svgPoint.y);

      if (dataIndex === -1) {
        return;
      }

      const payload = getAxisClickPayload({
        axisId: isXAxis ? xAxisIds[0] : yAxisIds[0],
        dataIndex,
        isXAxis,
        axes: isXAxis ? xAxisWithScale : yAxisWithScale,
        processedSeries,
      });

      if (payload === null) {
        return;
      }

      onAxisClick(event.detail.srcEvent, payload);
    });

    return () => {
      axisClickHandler.cleanup();
    };
  }, [
    params.onAxisClick,
    processedSeries,
    store,
    chartsLayerContainerRef,
    xAxisWithScale,
    xAxisIds,
    yAxisWithScale,
    yAxisIds,
    usedXAxis,
    usedYAxis,
    instance,
  ]);

  /**
   * Kept separate from the pointer effect: this listener must survive re-renders, so it reads the
   * axes and the callback when the key is pressed rather than closing over them. Re-subscribing per
   * render would drop keystrokes landing between a keyboard navigation update and its commit.
   */
  const handleKeyboardActivation = useEventCallback((event: KeyboardEvent) => {
    const onAxisClick = params.onAxisClick;

    if (
      !onAxisClick ||
      // Ignore the auto-repeat while the key is held: a pointer click does not repeat either.
      event.repeat ||
      !isItemActivationKey(event) ||
      !selectorChartsIsKeyboardActivationEnabled(store.state)
    ) {
      return;
    }

    const { axis: xAxes } = selectorChartXAxis(store.state);
    const { axis: yAxes } = selectorChartYAxis(store.state);
    const xAxisItem = selectorChartsKeyboardXAxisIndex(store.state);
    const yAxisItem = selectorChartsKeyboardYAxisIndex(store.state);
    const focusedAxisItem = hasAxisValueAt(xAxes, xAxisItem)
      ? { item: xAxisItem, isXAxis: true }
      : hasAxisValueAt(yAxes, yAxisItem) && { item: yAxisItem, isXAxis: false };

    if (!focusedAxisItem) {
      return;
    }

    const payload = getAxisClickPayload({
      axisId: focusedAxisItem.item.axisId,
      dataIndex: focusedAxisItem.item.dataIndex,
      isXAxis: focusedAxisItem.isXAxis,
      axes: focusedAxisItem.isXAxis ? xAxes : yAxes,
      processedSeries: selectorChartSeriesProcessed(store.state),
    });

    if (payload === null) {
      return;
    }

    event.preventDefault();
    // The callback only describes the pointer event unless the user augments the types.
    onAxisClick(event as unknown as ChartsActivationEvent, payload);
  });

  React.useEffect(() => {
    const element = chartsLayerContainerRef.current;

    if (element === null) {
      return undefined;
    }

    element.addEventListener('keydown', handleKeyboardActivation);

    return () => {
      element.removeEventListener('keydown', handleKeyboardActivation);
    };
  }, [chartsLayerContainerRef, handleKeyboardActivation]);

  return {};
};

useChartCartesianAxis.params = {
  xAxis: true,
  yAxis: true,
  dataset: true,
  onAxisClick: true,
  disableAxisListener: true,
  onHighlightedAxisChange: true,
  highlightedAxis: true,
  onTooltipAxisChange: true,
  tooltipAxis: true,
  axesGap: true,
};

useChartCartesianAxis.getDefaultizedParams = ({ params }) => {
  return {
    ...params,
    axesGap: params.axesGap ?? 0,
    colors: params.colors ?? rainbowSurgePalette,
    theme: params.theme ?? 'light',
    defaultizedXAxis: defaultizeXAxis(params.xAxis, params.dataset, params.axesGap ?? 0),
    defaultizedYAxis: defaultizeYAxis(params.yAxis, params.dataset, params.axesGap ?? 0),
  };
};

useChartCartesianAxis.getInitialState = (params) => ({
  cartesianAxis: {
    axesGap: params.axesGap,
    x: params.defaultizedXAxis,
    y: params.defaultizedYAxis,
  },
  ...(params.highlightedAxis === undefined
    ? {}
    : {
        controlledCartesianAxisHighlight: params.highlightedAxis,
      }),
});
